import type { Session, SessionKind } from './session'

export const LOGIN_PATH: Record<SessionKind, string> = {
  admin: '/login/admin',
  user: '/login/user',
}

export const CHANGE_PASSWORD_PATH = '/change-password'

const isAdmin = (s: Session) => s.kind === 'admin'

/** กำลังเข้าแทนพนักงานอยู่ไหม (delegate) — backend ตั้ง actingAs ให้ตอนเริ่มโหมดแทน */
export const isDelegating = (s: Session) => s.kind === 'admin' && Boolean(s.actingAs)

/**
 * เข้าหน้า "ของฉัน" ได้ไหม — พนักงานตัวจริง หรือ admin ที่กำลังเข้าแทน
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย แค่ซ่อน/โชว์เมนู — backend กันซ้ำที่ requireUser
 *    ซึ่งใช้เกณฑ์เดียวกันเป๊ะ (kind === 'user' หรือ admin ที่มี actingAs)
 */
export const isUser = (s: Session) => s.kind === 'user' || isDelegating(s)

/**
 * viewer ดูได้แต่แก้ไม่ได้ — เมนูที่แก้ข้อมูลต้องไม่โผล่ให้เห็นตั้งแต่แรก ไม่ใช่ให้กดแล้วค่อยเด้ง
 *
 * export ออกไปให้หน้าที่ "เข้าดูได้ทุก role แต่แก้ได้บาง role" ใช้ซ่อนปุ่มด้วยเกณฑ์เดียวกัน
 * (เช่น /admin/offsite/*) — ถ้าแต่ละหน้าเขียนเงื่อนไขเอง วันที่เพิ่ม role ใหม่จะหลุดเป็นจุด ๆ
 * ⚠️ ใช้ซ่อนปุ่มเท่านั้น ไม่ใช่ด่านความปลอดภัย — backend ปฏิเสธซ้ำอยู่แล้ว
 */
export const canEdit = (s: Session) => s.kind === 'admin' && s.role !== 'viewer'

/**
 * เฉพาะผู้ดูแลสูงสุด — ใช้กับหน้าจัดการบัญชีผู้ดูแลเท่านั้น
 *
 * ⚠️ หน้านั้นซ่อนแม้แต่การ**อ่าน** ต่างจากหน้าอื่นที่ viewer เข้ามาดูได้ —
 *    รายชื่อบัญชีคือรายการเป้าหมายให้คนไล่เดารหัส (มี username อะไรบ้าง ใครสิทธิ์สูงสุด
 *    ใครไม่ได้เข้ามานาน) และตัว login ก็จงใจไม่บอกว่ามีชื่อนี้อยู่จริงไหมอยู่แล้ว
 */
const isSuperadmin = (s: Session) => s.kind === 'admin' && s.role === 'superadmin'

/** เมนูที่กดแล้วไปหน้าใดหน้าหนึ่ง — ปลายทางจริงมีแต่ชนิดนี้ */
export type NavLeaf = {
  path: string
  label: string
  /** คำอธิบายสั้น — ใช้ทั้งใต้เมนูในหน้าแรกและบนหน้า placeholder */
  note: string
  /** ยังไม่มีของจริง — App.tsx สร้าง route ให้เองเป็นหน้า "กำลังจะมา" */
  placeholder?: true
  /** ไม่ต้องวาดในแถบเมนู (แต่ยังคุมสิทธิ์ด้วย allow เหมือนกัน) */
  hidden?: true
  /**
   * หน้านี้ใช้พื้นที่เต็มจอ ไม่ต้องจำกัดความกว้างให้อ่านง่าย
   *
   * ⚠️ ตั้งที่ route ไม่ใช่ตรวจจากเนื้อหา (เคยลอง `main:has(.table-wrap)` แล้วพลาด:
   *    หน้าที่ยังไม่มีข้อมูลจะ render ข้อความ "ไม่พบข้อมูล" แทนตาราง แล้วหน้าแคบลง
   *    เอง = ความกว้างเปลี่ยนตามจำนวนข้อมูล ซึ่งดูเหมือนบั๊กและอธิบายให้ใครฟังไม่ได้)
   */
  wide?: true
  /** เมนูฝั่งพนักงาน ("ของฉัน") — แยกสีจากเมนูเครื่องมือของแอดมินในแถบเมนู */
  section?: 'user'
  allow: (session: Session) => boolean
}

/**
 * หัวข้อที่พับเก็บได้ ข้างในเป็นเมนูย่อย — **ตัวมันเองไม่ใช่หน้า** กดแล้วแค่กาง/พับ
 *
 * ⚠️ ไม่มี `path` โดยตั้งใจ ถ้าให้หัวข้อเป็นลิงก์ด้วยกดแล้วจะได้สองผลพร้อมกัน
 *    (ย้ายหน้า + กางเมนู) ซึ่งบนมือถือกลายเป็นกดโดนผิดตลอด และต้องมีหน้าสารบัญ
 *    ที่เนื้อหาซ้ำกับเมนูย่อยที่เพิ่งกางออกมาอยู่ตรงหน้าอีก
 *    อยากได้หน้าสารบัญจริง ๆ ให้เพิ่มเป็นเมนูย่อยตัวแรก (เช่น "ภาพรวม") แทน
 *
 * `allow` ของหัวข้อ = ประตูของทั้งหมวด ระบบจะ **และ (AND)** กับ `allow` ของลูก
 * แต่ละตัวให้เอง — ตั้งที่หัวข้อว่า superadmin แล้วลูกเขียน isAdmin ไว้ ก็ยังเข้าไม่ได้
 * ละไว้ = ไม่กั้นที่ชั้นหมวด ปล่อยให้ลูกตัดสินเอง
 */
export type NavGroup = {
  label: string
  /** คำอธิบายของทั้งหมวด — ตอนนี้ใช้เป็น title ตอนเอาเมาส์ชี้ */
  note: string
  children: NavLeaf[]
  allow?: (session: Session) => boolean
}

export type NavEntry = NavLeaf | NavGroup

export const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry

/**
 * ตารางเมนูนี้เป็น **แหล่งเดียว**ของทั้งการวาดเมนูและการกันเข้า route
 * ถ้าแยกกันจะเพี้ยน: เมนูไม่โผล่แต่พิมพ์ url ตรง ๆ เข้าได้
 *
 * ลำดับในนี้คือลำดับที่โชว์บนแถบเมนู
 */
export const NAV: NavEntry[] = [
  // wide: หน้าแรกโชว์ข่าว 2 คอลัมน์ ต้องใช้พื้นที่เต็มจอ ไม่จำกัด 1040px
  {
    path: '/',
    label: 'หน้าแรก',
    note: 'สรุปข้อมูลบัญชีที่ใช้อยู่',
    wide: true,
    section: 'user',
    allow: () => true,
  },

  // ── ของพนักงาน (kind: user) — เห็นแค่ข้อมูลตัวเอง
  {
    path: '/me/worktime',
    label: 'เวลาทำงานของฉัน',
    note: 'เวลาเข้า–ออกงานรายวันของตัวเอง',
    section: 'user',
    allow: isUser,
  },
  {
    path: '/me/leave',
    label: 'การลาของฉัน',
    note: 'โควต้าวันลา ใบลา และใบปรับปรุงเวลาของตัวเอง',
    section: 'user',
    allow: isUser,
  },
  {
    path: '/me/ot',
    label: 'OT ของฉัน',
    note: 'ชั่วโมง OT รายเดือนและสถานะใบของตัวเอง',
    section: 'user',
    allow: isUser,
  },

  // ── ของผู้ดูแล (kind: admin)
  /* ทะเบียนอ้างอิงที่ระบบ HR เดิมเป็นเจ้าของ — ที่นี่อ่านอย่างเดียว
     ตั้ง allow ที่หัวข้อที่เดียว ลูกจึงเขียน allow: () => true ได้ (แบบเดียวกับ 'รายงาน') */
  {
    label: 'ข้อมูลหลัก',
    note: 'ทะเบียนบริษัทและข้อมูลอ้างอิงอื่น ๆ',
    allow: isAdmin,
    children: [
      {
        path: '/admin/master/companies',
        label: 'บริษัท',
        note: 'รหัส ชื่อ และ BU ของแต่ละบริษัทในระบบ HR',
        allow: () => true,
      },
      {
        path: '/admin/master/departments',
        label: 'แผนก',
        note: 'รหัสและชื่อแผนก พร้อมจำนวนพนักงานในแต่ละแผนก',
        allow: () => true,
      },
      {
        path: '/admin/master/branches',
        label: 'สาขา (จังหวัด)',
        note: 'จังหวัด/สำนักงาน พร้อมจำนวนพนักงานในแต่ละจังหวัด',
        allow: () => true,
      },
      {
        path: '/admin/master/functions',
        label: 'ฝ่าย (function)',
        note: 'ฝ่าย/function งาน พร้อมจำนวนพนักงานในแต่ละฝ่าย',
        allow: () => true,
      },
      /* ตารางในฐานชื่อ BU แต่เนื้อหาเป็นระดับตำแหน่ง (Officer…CEO) — ตั้งชื่อเมนู
         ตามของจริง ไม่ใช่ตามชื่อตาราง ไม่งั้นคนจะหาหน่วยธุรกิจในนี้แล้วไม่เจอ */
      {
        path: '/admin/master/bu',
        label: 'ระดับตำแหน่ง',
        note: 'ระดับตำแหน่งของพนักงาน (ตาราง BU) พร้อมจำนวนคนในแต่ละระดับ',
        allow: () => true,
      },
      {
        path: '/admin/master/holidays',
        label: 'วันหยุด',
        note: 'ปฏิทินวันหยุดประจำปี — ปีหนึ่งมีได้หลายปฏิทินแยกตามกลุ่มพนักงาน',
        wide: true,
        allow: () => true,
      },
      {
        path: '/admin/master/shifts',
        label: 'กะงาน',
        note: 'แม่แบบเวลาเข้า-ออกงานราย 7 วัน ใช้ตอนกำหนดกะให้พนักงาน',
        wide: true,
        allow: () => true,
      },
      {
        path: '/admin/master/leave-types',
        label: 'ประเภทการลา',
        note: 'ชนิดการลา สิทธิ์ตั้งต้น และเงื่อนไขการใช้สิทธิ์ แยกตามบริษัท',
        wide: true,
        allow: () => true,
      },
      /* อยู่ติด "ประเภทการลา" เพราะใช้ค่าจากตารางนั้นมาคิด (DEFAULT_DAY, GET_AFTER_DAY)
         คนตั้งค่าประเภทลาเสร็จแล้วมักต้องมาคิดสิทธิต่อทันที */
      {
        path: '/admin/master/leave-quota',
        label: 'สิทธิการลารายปี',
        note: 'คิดโควตาวันลาประจำปีให้ทั้งบริษัท และแก้รายคน',
        wide: true,
        allow: () => true,
      },
    ],
  },
  /* allow เป็น isAdmin ไม่ใช่ canEdit — viewer ต้องเข้ามาค้นหาและดูข้อมูลได้
     ส่วนปุ่มเพิ่ม/บันทึกซ่อนเองในหน้าด้วย canEdit() และ backend ปฏิเสธซ้ำอีกชั้น
     (ช่องรหัสผ่านก็ไม่ถูกส่งมาให้ viewer ตั้งแต่ฝั่ง API แล้ว) */
  {
    path: '/admin/employees',
    label: 'พนักงาน',
    note: 'ค้นหา เพิ่ม และแก้ข้อมูลพนักงานทั้งบริษัท',
    wide: true,
    allow: isAdmin,
  },
  /* ไม่ได้อยู่ใต้ "ข้อมูลหลัก" เพราะไม่ใช่ทะเบียน — เป็นการ **เขียนตารางเวลาจริง**
     ของพนักงานลง HR_EMP_WORK ซึ่งระบบคิดเวลาทำงานอ่านทุกวัน */
  {
    path: '/admin/shift-plan',
    label: 'สร้างตารางกะ',
    note: 'กำหนดกะให้พนักงานเป็นช่วงวันที่ — กะเดียวหรือกะสลับ',
    wide: true,
    allow: isAdmin,
  },
  /* allow เป็น isAdmin ไม่ใช่ canEdit — viewer ต้องเข้ามาดูได้ว่าใบไหนค้างที่ใคร
     (คำถามที่ HR ถูกถามบ่อยสุดคือ "ใบของฉันไปถึงไหนแล้ว" ซึ่งเป็นการดูล้วน ๆ)
     ปุ่มอนุมัติ/แก้ซ่อนเองในหน้าด้วย canEdit() และ route ฝั่ง backend ปฏิเสธซ้ำ
     ด้วย requireAdmin('superadmin', 'admin') อีกชั้น */
  {
    path: '/admin/approvals',
    label: 'อนุมัติคำขอ',
    note: 'ใบลา / ใบปรับปรุงเวลา ที่รอ HR — ดูตามรอบ 21 ถึง 20 และแก้ใบที่กรอกผิดได้',
    wide: true,
    allow: isAdmin,
  },
  /* ประวัติ = หน้าเดียวกับ "อนุมัติคำขอ" แต่เปิดมาที่สถานะ "อนุมัติแล้ว"
     หน้านั้นกรองได้ทุกสถานะอยู่แล้ว การทำหน้าประวัติแยกคือก๊อปทั้งหน้ามาเปลี่ยน
     ค่าเริ่มต้น แล้วต้องตามแก้สองที่ทุกครั้งที่ตารางเปลี่ยน */
  {
    path: '/admin/approvals/history',
    label: 'ประวัติคำขอ',
    note: 'ใบลา / ใบปรับปรุงเวลา ที่ตัดสินไปแล้ว — ย้อนดูตามรอบได้',
    wide: true,
    allow: isAdmin,
  },
  /* allow เป็น isAdmin ไม่ใช่ canEdit — viewer ต้องเข้ามาดู QR, ดูว่าใครผูกบัญชีไว้บ้าง
     และดูว่าใครลงทะเบียนไม่ผ่านได้ (งาน support ส่วนใหญ่คือการ "ดู")
     ปุ่มปลดการผูกซ่อนเองในหน้าด้วย canEdit() และ backend ปฏิเสธซ้ำอีกชั้น */
  {
    path: '/admin/line',
    label: 'LINE OA',
    note: 'QR เพิ่มเพื่อน · ใครผูกบัญชีไว้บ้าง · ใครลงทะเบียนไม่ผ่าน · เมนูครบไหม',
    wide: true,
    allow: isAdmin,
  },
  /* ── เข้างานนอกสถานที่ (ลงเวลาผ่าน LINE) ──
     allow เป็น isAdmin ทั้งสามหน้า: viewer ต้องเข้ามา**ดู**ได้ว่าใครมีสิทธิ์อยู่บ้าง
     ส่วนปุ่มที่แก้ข้อมูลแต่ละหน้าซ่อนเองด้วย canEdit() ข้างบน (backend ปฏิเสธซ้ำอีกชั้น)
     ถ้าใส่ canEdit ที่นี่ viewer จะเข้าไม่ได้เลยซึ่งไม่ใช่ที่ต้องการ */
  {
    path: '/admin/offsite/sites',
    label: 'สถานที่ลงเวลา',
    note: 'พิกัดและรัศมีของที่ที่ลงเวลาผ่าน LINE ได้',
    wide: true,
    allow: isAdmin,
  },
  {
    path: '/admin/offsite/permissions',
    label: 'สิทธิ์ลงเวลานอกสถานที่',
    note: 'ออก/ยกเลิกใบอนุญาตลงเวลาผ่าน LINE รายคน',
    wide: true,
    allow: isAdmin,
  },
  {
    path: '/admin/offsite/windows',
    label: 'เปิดลงเวลาชั่วคราว',
    note: 'เปิดให้ทุกคนลงเวลาชั่วคราวเมื่อเครื่องสแกนหน้าเสีย',
    wide: true,
    allow: isAdmin,
  },
  /* อ่านอย่างเดียวทั้งหน้า — ไม่มีปุ่มที่แก้ข้อมูล จึงเป็น isAdmin เหมือนพี่น้องข้างบน
     (viewer ต้องดูได้ เพราะงาน "ตรวจว่าใครลงเวลาจากที่ไหน" คือการดูล้วน ๆ) */
  {
    path: '/admin/offsite/checkins',
    label: 'ประวัติการลงเวลา',
    note: 'ใครลงเวลาผ่าน LINE ที่ไหน ห่างแค่ไหน พร้อมรูปถ่ายตอนกด',
    wide: true,
    allow: isAdmin,
  },

  /* ── Walk & Run ──
     allow เป็น isAdmin: viewer เข้ามาดูอันดับและประวัติการอัปได้ ส่วนช่องอัปไฟล์
     ซ่อนเองด้วย canEdit() ในหน้า (backend ปฏิเสธซ้ำอีกชั้นด้วย role ที่ route) */
  {
    path: '/admin/walkrun',
    label: 'Walk & Run',
    note: 'อัป CSV ก้าว/ระยะทาง เป็นข้อมูลของ dashboard ใน LINE',
    wide: true,
    allow: isAdmin,
  },

  {
    path: '/admin/admins',
    label: 'ผู้ดูแลระบบ',
    note: 'สร้าง/ปิด บัญชีเข้าหลังบ้าน · ตั้งรหัสใหม่ · ปลดล็อก',
    wide: true,
    allow: isSuperadmin,
  },

  /* viewer เข้าไม่ได้ (allow ตัดออก) และ backend ตัดซ้ำที่ requireAdmin('superadmin','admin')
     — การเปิดดูข้อมูลส่วนบุคคลของคนทั้งบริษัทต้องเป็นสิทธิ์ที่ตั้งใจให้ ไม่ใช่ของแถม
     ส่วน "ประวัติ" ในหน้านี้ viewer ยังเรียก API ได้ ถ้าวันหน้าอยากให้ดูประวัติอย่างเดียว
     ต้องแยกหน้าออกมา ไม่ใช่ปลดเงื่อนไขตรงนี้ */
  {
    path: '/admin/news',
    label: 'ข่าว / ประกาศ',
    note: 'จัดการข่าว ประกาศ ฟอร์ม ที่แสดงหน้าแรกของพนักงาน',
    wide: true,
    // viewer เห็นได้ (อ่าน) แต่ปุ่มแก้ซ่อนด้วย canEdit ในหน้า — backend กันซ้ำ
    allow: isAdmin,
  },

  {
    path: '/admin/slides',
    label: 'สไลด์หน้าแรก',
    note: 'จัดการรูป banner ที่แสดงบนสุดของหน้าแรกพนักงาน',
    wide: true,
    allow: isAdmin,
  },

  {
    path: '/admin/delegate',
    label: 'เข้าแทนพนักงาน',
    note: 'เปิดหน้า "ของฉัน" ของพนักงานเพื่อช่วยดูปัญหา · บันทึกทุกครั้ง',
    wide: true,
    // ⚠️ ต้องตรงกับ requireAdmin('superadmin','admin') ใน routes/adminDelegate.js เป๊ะ ๆ
    //    ถ้าที่นี่หลวมกว่า เมนูจะโผล่ให้ role ที่กดแล้วโดน 403
    allow: (s) => s.kind === 'admin' && (s.role === 'superadmin' || s.role === 'admin'),
  },

  /* ── ตัวอย่างเมนูย่อย ──
     รายงานมีหลายใบและจะเพิ่มขึ้นเรื่อย ๆ ถ้าปล่อยเรียงเดี่ยว ๆ แถบเมนูจะยาวจนคนหา
     ของไม่เจอ (ตอนนี้ก็ 11 รายการแล้ว) จึงพับรวมเป็นหมวดเดียว

     ⚠️ ตั้ง allow ไว้ที่ **หัวข้อ** ที่เดียว ลูกจึงเขียน `allow: () => true` ได้ —
        ไม่ใช่ปล่อยผ่าน แต่เพราะ NAV_LEAVES เอา allow ของหัวข้อมา AND ให้แล้ว
        เขียนซ้ำที่ลูกทุกตัวคือที่ที่วันหลังจะแก้ตกหล่นทีละตัว */
  {
    label: 'รายงาน',
    note: 'สรุปเวลาทำงาน / การลา / OT รายเดือน',
    allow: isAdmin,
    children: [
      /* รวมของที่ระบบ HR เดิมแยกไว้ 4 หน้า (Home.aspx · Leave.aspx?action=1 ·
         Leave.aspx?action=2 · TimeAttendance.aspx) มาไว้หน้าเดียว ดูทีละคน
         — ต่างจากใบอื่นในหมวดนี้ที่เป็นสรุปทั้งบริษัท */
      {
        path: '/admin/reports/person',
        label: 'ข้อมูลบุคคล',
        note: 'โปรไฟล์ สิทธิ์ลา ใบลา ใบปรับปรุงเวลา และเวลาเข้า–ออก รายคน',
        wide: true,
        allow: () => true,
      },
      /* หน้าเดียวครอบ 4 ใน 5 แท็บของ frmReportAll เดิม (ทั้งหมด / เบี้ยขยัน /
         ขาด-สาย / การลา) เพราะทุกแท็บใช้ชุดข้อมูลเดียวกัน ต่างแค่กรองแถว
         จึงไม่แยกเป็นหลายเมนูให้คนต้องเดาว่าต้องเปิดอันไหน */
      {
        path: '/admin/reports/worktime',
        label: 'สรุปการทำงาน',
        note: 'ขาดงาน มาสาย วันลา ใบปรับปรุงเวลา และเบี้ยขยัน รายคนในช่วงที่เลือก',
        wide: true,
        allow: () => true,
      },
      {
        path: '/admin/reports/ot',
        label: 'สรุป OT',
        note: 'ชั่วโมงล่วงเวลารายคนในแต่ละเดือน แยกตามเรท',
        wide: true,
        allow: () => true,
      },
      {
        path: '/admin/reports/offsite',
        label: 'สรุปการลงเวลานอกสถานที่',
        note: 'ใครลงเวลาผ่าน LINE บ่อยแค่ไหน จากที่ไหนบ้าง',
        wide: true,
        allow: () => true,
      },
      /* ดูทั้งบริษัทในวันเดียว — ต่างจาก "ข้อมูลบุคคล" ที่ดูทีละคนหลายวัน
         วางก่อนข้อมูลดิบ เพราะเป็นหน้าที่เปิดดูประจำทุกวัน */
      {
        path: '/admin/reports/attendance-day',
        label: 'เวลาเข้า–ออก รายวัน',
        note: 'ทั้งบริษัทในวันเดียว ใครยังไม่มา ใครสแกนไม่ครบ · แก้ว่ารอยไหนคือเข้า/ออกได้',
        wide: true,
        allow: () => true,
      },
      /* ไม่ใช่ "รายงาน" แบบสรุปเหมือนตัวอื่นในหมวดนี้ แต่เป็นของดิบไว้ไล่ปัญหา
         วางไว้ท้ายสุดเพราะคนเปิดเมื่อสงสัยว่าเวลาผิด ไม่ใช่เปิดอ่านประจำ */
      {
        path: '/admin/reports/ta-data',
        label: 'ข้อมูลดิบเครื่องตอกบัตร',
        note: 'ทุกครั้งที่แตะบัตร ตามที่เครื่องบันทึกไว้ ก่อนถูกตีความเป็นเวลาเข้า–ออก',
        wide: true,
        allow: () => true,
      },
    ],
  },

  // เข้าจากปุ่มบน header ไม่ต้องมีในแถบเมนู (พนักงานเปลี่ยนรหัสที่ระบบ HR ไม่ใช่ที่นี่)
  {
    path: CHANGE_PASSWORD_PATH,
    label: 'เปลี่ยนรหัสผ่าน',
    note: 'เปลี่ยนรหัสผ่านของบัญชีผู้ดูแล',
    hidden: true,
    allow: isAdmin,
  },
]

/**
 * ทุกหน้าในระบบเรียงแบน ๆ ไม่สนว่าอยู่ในหมวดไหน — ใช้ตอบคำถามที่ถามด้วย **path**
 * (เข้าได้ไหม / เต็มจอไหม / ต้องสร้าง route ให้ไหม) เพราะ path ไม่รู้จักโครงเมนู
 *
 * ⚠️ allow ของลูกถูก **และ (AND)** กับ allow ของหัวข้อไว้ตรงนี้ที่เดียว — ถ้าลืมทำ
 *    ตรงนี้ คนที่หมวดถูกซ่อนไว้จะพิมพ์ url ของเมนูย่อยเข้าไปตรง ๆ ได้ เพราะ
 *    canAccess() หาเจอแต่ตัวลูกที่ allow ผ่าน
 */
export const NAV_LEAVES: NavLeaf[] = NAV.flatMap((entry) =>
  isGroup(entry)
    ? entry.children.map((child) => ({
        ...child,
        allow: (session: Session) => (entry.allow?.(session) ?? true) && child.allow(session),
      }))
    : [entry],
)

/**
 * โครงเมนูที่บัญชีนี้เห็น — คงชั้นหมวดไว้ให้ Shell วาด
 *
 * หมวดที่ลูกถูกซ่อนหมดจะหายไปทั้งหมวด ไม่เหลือหัวข้อที่กางออกมาแล้วว่างเปล่า
 */
export function navFor(session: Session): NavEntry[] {
  // ระบุ <NavEntry> เอง — ปล่อยให้ TS เดาแล้วมันล็อกชนิดจาก return ตัวแรกที่เจอ
  // (NavLeaf) แล้วฟ้องว่าหมวดใส่ไม่ได้ ทั้งที่ผลลัพธ์เป็นได้ทั้งสองอย่าง
  return NAV.flatMap<NavEntry>((entry) => {
    if (!isGroup(entry)) return !entry.hidden && entry.allow(session) ? [entry] : []
    if (entry.allow && !entry.allow(session)) return []

    const children = entry.children.filter((child) => !child.hidden && child.allow(session))
    return children.length > 0 ? [{ ...entry, children }] : []
  })
}

/** เมนูที่ยังเป็นของปลอม — หน้าแรกเอาไปบอกว่ากำลังจะมีอะไร (รวมเมนูย่อยด้วย) */
export function upcomingFor(session: Session): NavLeaf[] {
  return NAV_LEAVES.filter((item) => !item.hidden && item.placeholder && item.allow(session))
}

/**
 * เทียบแบบตรงตัวได้เพราะทุก route ยังไม่มี parameter
 * ⚠️ วันที่เพิ่ม route แบบ /admin/employees/:id ต้องเปลี่ยนมาเทียบ prefix
 *
 * path ที่ไม่อยู่ในตาราง = ปล่อยผ่านให้ route "ไม่พบหน้า" จัดการ (ไม่ใช่เรื่องสิทธิ์)
 */
export function canAccess(session: Session, pathname: string): boolean {
  const item = NAV_LEAVES.find((i) => i.path === pathname)
  return item ? item.allow(session) : true
}

/** หน้านี้ให้ใช้พื้นที่เต็มจอไหม — path ที่ไม่อยู่ในตารางถือว่าเป็นหน้าข้อความ (จำกัดความกว้าง) */
export function isWide(pathname: string): boolean {
  return NAV_LEAVES.find((i) => i.path === pathname)?.wide === true
}

/**
 * คนที่ยังไม่ล็อกอินเปิด url ลึกมา — เดาทางเข้าจาก path ก่อน (แม่นกว่า)
 * เหลือหน้ากลาง ๆ เช่น "/" ค่อยใช้ kind ล่าสุดของแท็บนี้ แล้วปิดท้ายด้วยทางพนักงาน
 * เพราะพนักงานเป็นคนส่วนใหญ่ของระบบ
 */
export function loginPathFor(pathname: string, lastKind: SessionKind | null): string {
  if (pathname.startsWith('/admin')) return LOGIN_PATH.admin
  if (pathname.startsWith('/me')) return LOGIN_PATH.user
  return LOGIN_PATH[lastKind ?? 'user']
}

/** ปลายทางที่ตั้งใจจะไปก่อนถูกเด้งมาหน้า login — กันค่าที่ไม่ใช่ path ในแอปนี้ */
export function fromPath(state: unknown): string {
  const from = state && typeof state === 'object' ? (state as { from?: unknown }).from : undefined
  if (typeof from !== 'string') return '/'
  if (!from.startsWith('/') || from.startsWith('//')) return '/'
  if (from.startsWith('/login')) return '/'
  return from
}
